import { IWorkBenchLayout } from '@fox/blocks';
import { SwiftFoxSearchResult } from '../blocks/create-person-block/shared-components/contracts';
import { ISection } from '../blocks/section-block/contracts';
import { UploadedFilesWithMeta } from '../blocks/upload-files-block/contracts';
import { IQuestionAnswer } from '../form-root/contracts';

export const FORM_QUESTION_ANSWERED_TOPIC = 'form-question-answered';
export const FORM_SET_ANSWERS = 'form-set-answers';

export enum SurveyState {
	loading,
	error,
	undefined,
	submitted,
	submitting
}

export enum FormRenderMode {
	design,
	runtime,
	preview
}

export interface IFormRuntimeContext {
	formRequestId: string;
	layout: IWorkBenchLayout;
	answerQuestion: (answer: IQuestionAnswer, isAnswered?: boolean) => void;
	getAnswers: () => IQuestionAnswer[];
	getAnswer: (questionName: string) => IQuestionAnswer | undefined;
	questionStyles: QuestionStyles;
	getQuestionStyle: (questionStyleType: QuestionStyleTypes, fallback: QuestionStyle, questionId?: string) => QuestionStyle;
	currentPage: number;
	goToNextPage?: () => void;
	goToPreviousPage?: () => void;
	prevPage: number[];
	getPointsTotal: (input: {
		sections: ISection[];
		layout: IWorkBenchLayout | undefined;
		answers: IQuestionAnswer[];
	}) => Promise<{ status: 'success' | 'error'; pointTotal?: number; message?: string }>;
	complete: (input: { searchString: string; types?: string[]; categories?: string[]; signedParams?: string }) => Promise<SwiftFoxSearchResult[]>;
	uploadFile: (input: { file: File }) => Promise<UploadedFilesWithMeta | undefined>;
	/**
	 * Indicates the current rendering mode of the form.
	 *
	 * - `FormRenderMode.design`: The form is being edited in the form designer UI.
	 * - `FormRenderMode.preview`: The form is being previewed as it will appear to end users, but is not live.
	 * - `FormRenderMode.runtime`: The form is being actively filled in by a user or customer.
	 *
	 * Use this property to conditionally render UI or logic depending on whether the form is in design, preview, or runtime mode.
	 */
	formRenderMode: FormRenderMode;
	sections: ISection[];
	submitForm?: (answers: IQuestionAnswer[]) => Promise<void>;
	submitButtonDisabled?: boolean;
	/**This would refer to event the form belongs to */
	owner?: {
		ownerId?: string;
		ownerType?: string;
	};
	/**The person targetted by the form */
	target?: SurveyTargetInfo;
	/**When using forms within SwiftFox, we do not need a token. Some api calls are blocked when a token has not been verified, this allows us to make those calls regardless */
	inLocalSurveyMode?: boolean;
	surveyState?: SurveyState;
	submitButtonLabel?: string;
}

export interface IFormRuntimeContextProps {
	formRequestId: string;
	layout: IWorkBenchLayout;
	styles: QuestionStyles;
	/**
	 * Indicates the current rendering mode of the form.
	 *
	 * - `FormRenderMode.design`: The form is being edited in the form designer UI.
	 * - `FormRenderMode.preview`: The form is being previewed as it will appear to end users, but is not live.
	 * - `FormRenderMode.runtime`: The form is being actively filled in by a user or customer.
	 *
	 * Use this property to conditionally render UI or logic depending on whether the form is in design, preview, or runtime mode.
	 */
	formRenderMode: FormRenderMode;
	getPointsTotal: (input: {
		sections: ISection[];
		layout: IWorkBenchLayout | undefined;
		answers: IQuestionAnswer[];
	}) => Promise<{ status: 'success' | 'error'; pointTotal?: number; message?: string }>;
	complete: (input: { searchString: string; types?: string[]; categories?: string[]; signedParams?: string }) => Promise<SwiftFoxSearchResult[]>;
	uploadFile: (input: { file: File }) => Promise<UploadedFilesWithMeta | undefined>;
	surveyState?: SurveyState;
	/**When using forms within SwiftFox, we do not need a token. Some api calls are blocked when a token has not been verified, this allows us to make those calls regardless */
	inLocalSurveyMode?: boolean;
	initialAnswers?: IQuestionAnswer[];
	ownerId?: string;
	ownerType?: string;
	targetInfo?: SurveyTargetInfo;
	submitButtonDisabled?: boolean;
	submitButtonLabel?: string;
	onSubmit?: (answers: IQuestionAnswer[]) => Promise<any>;
	onSubmitted?: (res: any) => any;
}

export interface QuestionStyle {
	fontFamily?: string | null;
	fontSize?: string | number | null;
	color?: string | null;
	textDecoration?: string | null;
	fontWeight?: number | null;
	fontStyle?: string | null;
	backgroundColour?: string | null;
	primaryColour?: string | null;
	minHeight?: React.CSSProperties['minHeight'] | null;
}

export interface QuestionStyles {
	questionLabelStyle: QuestionStyle;
	questionDescriptionStyle: QuestionStyle;
	questionAnswerStyle: QuestionStyle;
	generalFormStyle: QuestionStyle;
}

export enum QuestionStyleTypes {
	label = 'questionLabelStyle',
	description = 'questionDescriptionStyle',
	answer = 'questionAnswerStyle',
	general = 'generalFormStyle'
}

export const DEFAULT_QUESTION_STYLES = {
	questionLabelStyle: {
		fontWeight: 700,
		fontSize: '11pt',
		color: '#58595B',
		fontFamily: null,
		fontStyle: undefined,
		textDecoration: undefined
	},
	questionDescriptionStyle: {
		fontSize: '10pt',
		fontWeight: undefined,
		color: '#58595B',
		fontFamily: null,
		fontStyle: undefined,
		textDecoration: undefined
	},
	questionAnswerStyle: {
		fontSize: '10pt',
		fontWeight: undefined,
		color: '#58595B',
		fontFamily: null,
		fontStyle: undefined,
		textDecoration: undefined
	},
	generalFormStyle: { backgroundColour: undefined, primaryColour: undefined }
} as const;

export type SurveyTargetInfo = {
	firstName?: string;
	lastName?: string;
	email?: string;
	ownerId?: string;
	ownerType?: string;
};
